import { create } from 'zustand';

import { lockParentInSummary } from '@routes/userRoutes/objects/objectTree';

/**
 * Parents a child object was just trained from, by node uuid, so a second training
 * run cannot start from them while the first is starting.
 *
 * Creating a child starts its training asynchronously: for a while the annotation
 * summary still says the parent `can_be_trained`. Locking only the cached summary is not
 * enough, since the next refetch (every tile save in annotation mode) brings `true`
 * back. The lock lives here instead and is laid over each fetched summary until the
 * server agrees (the parent comes back untrainable, or is gone), which releases it.
 */
export const useTrainingLockStore = create(() => ({ locked: {} }));

/** Lock `parentUuid` until the summary itself says it cannot be trained. */
export const lockTraining = parentUuid =>
	useTrainingLockStore.setState(state => ({
		locked: { ...state.locked, [parentUuid]: true },
	}));

/**
 * `summary` (as `getAnnotationSummary` returns it) with every locked parent still
 * untrainable. A parent the server now reports untrainable, or no longer lists, is
 * released.
 */
export const holdTrainingLocks = summary => {
	const { locked } = useTrainingLockStore.getState();
	const uuids = Object.keys(locked);
	if (!uuids.length || !summary?.nodes) return summary;

	const released = uuids.filter(uuid => {
		const entry = summary.nodes.find(node => node.node_uuid === uuid);
		return !entry || entry.can_be_trained !== true;
	});
	if (released.length)
		useTrainingLockStore.setState(state => {
			const next = { ...state.locked };
			released.forEach(uuid => delete next[uuid]);
			return { locked: next };
		});

	return uuids
		.filter(uuid => !released.includes(uuid))
		.reduce((held, uuid) => lockParentInSummary(held, uuid), summary);
};
